<template>
    <!-- Sidebar: off-canvas drawer below `lg`, docked (expanded / icon rail) from `lg` up -->
    <aside aria-label="Menú principal"
        class="tw:fixed tw:top-0 tw:left-0 tw:z-1050 tw:flex tw:h-dvh tw:w-70 tw:flex-col tw:border-r tw:border-slate-800 tw:bg-brand-background tw:text-slate-300 tw:shadow-xl tw:select-none tw:transition-[width,translate,visibility] tw:duration-300 tw:lg:translate-x-0"
        :class="[mobileOpen ? 'tw:translate-x-0' : 'tw:-translate-x-full tw:max-lg:invisible', expanded ? 'tw:lg:w-70' : 'tw:lg:w-20']"
        @mouseenter="setSidebarHovering(true)" @mouseleave="setSidebarHovering(false)">

        <!-- Close button (drawer only) -->
        <button type="button" @click="closeMobileSidebar" aria-label="Cerrar menú"
            class="tw:lg:hidden tw:absolute tw:top-3 tw:right-3 tw:z-10 tw:inline-flex tw:items-center tw:justify-center tw:w-9 tw:h-9 tw:rounded-lg tw:border-0 tw:bg-white/5 tw:text-slate-300 tw:cursor-pointer tw:transition-colors tw:hover:bg-white/10 tw:hover:text-white">
            <i class="fas fa-times"></i>
        </button>

        <!-- Sidebar Brand Logo -->
        <div :class="expanded ? 'tw:h-32 tw:lg:h-40 tw:px-5' : 'tw:h-20 tw:px-2'"
            class="tw:flex tw:shrink-0 tw:items-center tw:gap-3 tw:border-b tw:border-slate-800/80 tw:bg-brand-background tw:transition-all tw:duration-300">
            <router-link to="/admin/dashboard"
                class="tw:flex tw:justify-center tw:flex-col tw:items-center tw:gap-3 tw:no-underline tw:w-full tw:min-w-0">
                <div :class="expanded ? 'tw:size-16 tw:lg:size-24' : 'tw:size-10'"
                    class="tw:rounded-xl tw:bg-brand-primary/20 tw:border tw:border-brand-primary/30 tw:flex tw:items-center tw:justify-center tw:overflow-hidden tw:shrink-0 tw:transition-all tw:duration-300">
                    <img v-if="configuration?.image" :src="configuration.image" :alt="configuration.name || 'Logo'"
                        class="tw:w-full tw:h-full tw:object-cover" />
                    <i v-else class="fas fa-cubes tw:text-brand-primary tw:text-sm"></i>
                </div>
                <span v-show="expanded"
                    class="tw:max-w-full tw:font-bold tw:text-sm tw:text-white tw:truncate tw:tracking-tight">
                    {{ configuration?.name || 'Panel Admin' }}
                </span>
            </router-link>
        </div>

        <!-- Scrollable Navigation -->
        <nav id="sidebar-menu" v-auto-hide-scrollbar @click="handleNavClick"
            class="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:overflow-x-hidden tw:scrollbar-autohide tw:[--scrollbar-thumb:rgba(255,255,255,0.2)] tw:px-3 tw:py-6 tw:space-y-4">
            <template v-for="item in menuItems()">
                <SidebarGroup v-if="item.children" :key="item.key" :icon="item.icon" :title="item.title"
                    :active="isActive(item.routes)" :open="openMenu && menuActive == item.key" :expanded="expanded"
                    @toggle="toggleMenu(item.key)">
                    <SidebarSubLink v-for="child in item.children" :key="child.to" :to="child.to"
                        :active="isActive(child.routes)">
                        {{ child.title }}
                    </SidebarSubLink>
                </SidebarGroup>
                <SidebarLink v-else :key="item.key" :to="item.to" :icon="item.icon" :title="item.title"
                    :active="isActive(item.routes)" :expanded="expanded" />
            </template>
        </nav>
    </aside>
</template>

<script>
import { ACCESS_MENU, ACCESS_MENU_ACTION } from '@/validations/auth/AccessControlList';
import {
    ADMIN_USERS_INDEX, CLIENT_USERS_INDEX, HOTEL_RESERVATIONS_INDEX,
    CIRCUIT_RESERVATIONS_INDEX,
    DAYTRIPS_RESERVATIONS_INDEX,
    TOURS_RESERVATIONS_INDEX,
    INTERNATIONAL_RESERVATIONS_INDEX,
    FLIGHT_RESERVATIONS_INDEX,
    OTHER_SERVICES_RESERVATIONS_INDEX,
    PACKAGED_RESERVATIONS_INDEX,
    LOCKED_RESERVATIONS_INDEX,
    GROUP_RESERVATIONS_INDEX,
} from '@/enums/MenuActionEnums';
import {
    HOME, BRANCH_OFFICE, USERS, OPERATORS, CONVEYORS, UNITS, SERVICES, RESERVATIONS, BOX,
    SAVING, PROMOTIONS, BANKS, PROFILES, COUPON, PAYMENT_MULTIPLE, SERVICE_QUOTE, ACTIVITY, REPORTS, TRAVEL_GALLERY
} from "@/enums/MenuEnums";
import {
    ROUTE_HOME,
    ROUTE_BRANCH_OFFICES,
    ROUTE_BRANCH_OFFICESADD,
    ROUTE_BRANCH_OFFICESEDIT,
    ROUTE_BRANCH_WORKINGHOURS,
    ROUTE_USER_ADMIN,
    ROUTE_USER_CLIENTS,
    ROUTE_USER_FREELANCERS,
    ROUTE_USER_AGENCIES,
    ROUTE_USER_ADD,
    ROUTE_USER_EDIT,
    ROUTE_OPERATORS,
    ROUTE_OPERATOR_EDIT,
    ROUTE_TRANSPORTS,
    ROUTE_TRANSPORT_EDIT,
    ROUTE_UNITS,
    ROUTE_SERVICE_HOTELS,
    ROUTE_SERVICE_CIRCUITS,
    ROUTE_SERVICE_DAYTRIPS,
    ROUTE_SERVICE_TOURS,
    ROUTE_SERVICE_INTERNATIONALS,
    ROUTE_SERVICE_LOCKEDS,
    ROUTE_SERVICE_LOCKED_SHOW,
    ROUTE_SERVICE_GROUPS,
    ROUTE_SERVICE_GROUP_SHOW,
    ROUTE_SERVICE_EDIT,
    ROUTE_SERVICE_TRAVEL_DATE,
    ROUTE_SERVICE_EXIT_PLACES,
    ROUTE_SERVICE_EXIT_PLACE_PRICES,
    ROUTE_RESERVATION_HOTEL_INDEX,
    ROUTE_RESERVATION_HOTEL_CREATE,
    ROUTE_RESERVATION_HOTEL_SHOW,
    ROUTE_RESERVATION_HOTEL_EDIT,
    ROUTE_RESERVATION_CIRCUIT_INDEX,
    ROUTE_RESERVATION_CIRCUIT_CREATE,
    ROUTE_RESERVATION_CIRCUIT_SHOW,
    ROUTE_RESERVATION_CIRCUIT_EDIT,
    ROUTE_RESERVATION_DAYTRIPS_INDEX,
    ROUTE_RESERVATION_DAYTRIPS_CREATE,
    ROUTE_RESERVATION_DAYTRIPS_SHOW,
    ROUTE_RESERVATION_DAYTRIPS_EDIT,
    ROUTE_RESERVATION_TOURS_INDEX,
    ROUTE_RESERVATION_TOURS_CREATE,
    ROUTE_RESERVATION_TOURS_SHOW,
    ROUTE_RESERVATION_TOURS_EDIT,
    ROUTE_USER_PERMISSIONS,
    ROUTE_USER_BRANCHOFFICES,
    ROUTE_RESERVATION_INTERNATIONALS_INDEX,
    ROUTE_RESERVATION_INTERNATIONALS_CREATE,
    ROUTE_RESERVATION_INTERNATIONALS_SHOW,
    ROUTE_RESERVATION_INTERNATIONALS_EDIT,
    ROUTE_RESERVATION_FLIGHTS_INDEX,
    ROUTE_RESERVATION_FLIGHTS_CREATE,
    ROUTE_RESERVATION_FLIGHTS_SHOW,
    ROUTE_RESERVATION_FLIGHTS_EDIT,
    ROUTE_RESERVATION_OTHER_SERVICE_INDEX,
    ROUTE_RESERVATION_OTHER_SERVICE_CREATE,
    ROUTE_RESERVATION_OTHER_SERVICE_SHOW,
    ROUTE_RESERVATION_OTHER_SERVICE_EDIT,
    ROUTE_RESERVATION_NOTES,
    ROUTE_RESERVATION_SELECT_SEAT_INDEX,
    ROUTE_PROFILE_LIST,
    ROUTE_PROFILE_ADD,
    ROUTE_PROMOTIONS,
    ROUTE_PROMOTION_ADD,
    ROUTE_PROMOTION_EDIT,
    ROUTE_COUPONS,
    ROUTE_COUPON_ADD,
    ROUTE_BANKS,
    ROUTE_BANK_ADD,
    ROUTE_BANK_EDIT,
    ROUTE_RESERVATION_PAYMENTS,
    ROUTE_RESERVATION_LIST_PAYMENTS,
    ROUTE_RESERVATION_PEOPLE,
    ROUTE_BRANCH_BOXES,
    ROUTE_BOX_HISTORY,
    ROUTE_BOX_HISTORY_MOVE_LIST,
    ROUTE_SAVING,
    ROUTE_BANK_HISTORY_MOVE_LIST,
    ROUTE_RESERVATION_PAYMENT_TO_BROOKER,
    ROUTE_PROMOTION_FOLDER,
    ROUTE_RESERVATION_PACKAGED_INDEX,
    ROUTE_RESERVATION_PACKAGED_CREATE,
    ROUTE_RESERVATION_PACKAGED_SHOW,
    ROUTE_RESERVATION_PACKAGED_EDIT,
    ROUTE_ACTIVITY,
    ROUTE_REPORTS,
    ROUTE_RESERVATION_TRANSFER_INDEX,
    ROUTE_RESERVATION_TRANSFER_CREATE,
    ROUTE_RESERVATION_TRANSFER_SHOW,
    ROUTE_RESERVATION_TRANSFER_EDIT,
    ROUTE_PAYMENT_MULTIPLE,
    ROUTE_PAYMENT_MULTIPLE_ADD,
    ROUTE_SERVICE_QUOTE,
    ROUTE_SERVICE_QUOTE_ADD,
    ROUTE_RESERVATION_LOCKEDS_INDEX,
    ROUTE_RESERVATION_LOCKEDS_CREATE,
    ROUTE_RESERVATION_LOCKEDS_SHOW,
    ROUTE_RESERVATION_LOCKEDS_EDIT,
    ROUTE_RESERVATION_GROUPS_INDEX,
    ROUTE_RESERVATION_GROUPS_CREATE,
    ROUTE_RESERVATION_GROUPS_SHOW,
    ROUTE_RESERVATION_GROUPS_EDIT,
    ROUTE_TRAVEL_GALLERY_INDEX,
} from "@/enums/RouteNameEnums";
import { mapGetters } from "vuex";
import {
    isSidebarEffectivelyCollapsed, setSidebarHovering, isMobileSidebarOpen, closeMobileSidebar,
} from '@/plugins/sidebarStore';
import { SERVICE_TYPE_BY_ID } from '@/enums/ServiceTypeEnums';
import SidebarLink from './sidebar/SidebarLink.vue';
import SidebarGroup from './sidebar/SidebarGroup.vue';
import SidebarSubLink from './sidebar/SidebarSubLink.vue';

// Group keys double as the `menuActive` id for the accordion.
const GROUP_USERS = 1;
const GROUP_SERVICES = 2;
const GROUP_RESERVATIONS = 3;

export default {
    name: 'SideBar',
    components: { SidebarLink, SidebarGroup, SidebarSubLink },
    data() {
        return {
            openMenu: true,
            menuActive: 0,
        };
    },
    computed: {
        ...mapGetters("auth", ["user"]),
        ...mapGetters("config", ["configuration"]),

        currentPath() {
            return this.$route.name;
        },
        expanded() {
            return !isSidebarEffectivelyCollapsed();
        },
        mobileOpen() {
            return isMobileSidebarOpen();
        },
    },
    methods: {
        setSidebarHovering,
        closeMobileSidebar,
        // Closes the drawer even when the tapped link is the current route
        // (no route change, so TemplateDashboard's $route watcher won't fire).
        handleNavClick(event) {
            if (event.target.closest('a')) closeMobileSidebar();
        },
        isActive(routes) {
            return routes.includes(this.currentPath);
        },
        toggleMenu(menu) {
            if (menu != this.menuActive && this.openMenu == true) {
                this.menuActive = menu;
                return;
            }

            this.menuActive = menu;
            this.openMenu = !this.openMenu;
        },
        // Called from the template (not a computed) on purpose: ACCESS_MENU reads
        // store state that may load after mount, and every render must re-check it.
        menuItems() {
            const hasService = (id) => !!this.configuration?.available_services?.includes(id);
            const link = (menuId, to, routes) => {
                const access = ACCESS_MENU(menuId);
                return access && { key: to, to, routes, icon: access.menu.icon, title: access.menu.title };
            };
            const group = (menuId, key, routes, children) => {
                const access = ACCESS_MENU(menuId);
                return access && {
                    key, routes, icon: access.menu.icon, title: access.menu.title,
                    children: children.filter((child) => child.visible),
                };
            };

            return [
                link(HOME, '/admin/dashboard', [ROUTE_HOME]),
                link(BRANCH_OFFICE, '/admin/sucursales', [ROUTE_BRANCH_OFFICES, ROUTE_BRANCH_OFFICESADD, ROUTE_BRANCH_OFFICESEDIT, ROUTE_BRANCH_WORKINGHOURS]),
                group(USERS, GROUP_USERS, [ROUTE_USER_CLIENTS, ROUTE_USER_ADMIN, ROUTE_USER_FREELANCERS, ROUTE_USER_AGENCIES, ROUTE_USER_ADD, ROUTE_USER_EDIT, ROUTE_USER_PERMISSIONS, ROUTE_USER_BRANCHOFFICES], [
                    { title: 'Clientes', to: '/admin/usuarios/clientes', visible: ACCESS_MENU_ACTION(CLIENT_USERS_INDEX), routes: [ROUTE_USER_CLIENTS] },
                    { title: 'Administradores', to: '/admin/usuarios/administradores', visible: ACCESS_MENU_ACTION(ADMIN_USERS_INDEX), routes: [ROUTE_USER_ADMIN, ROUTE_USER_PERMISSIONS, ROUTE_USER_BRANCHOFFICES] },
                ]),
                link(OPERATORS, '/admin/operadoras', [ROUTE_OPERATORS, ROUTE_OPERATOR_EDIT]),
                link(CONVEYORS, '/admin/transportadoras', [ROUTE_TRANSPORTS, ROUTE_TRANSPORT_EDIT]),
                link(UNITS, '/admin/unidades', [ROUTE_UNITS]),
                group(SERVICES, GROUP_SERVICES, [ROUTE_SERVICE_HOTELS, ROUTE_SERVICE_CIRCUITS, ROUTE_SERVICE_DAYTRIPS, ROUTE_SERVICE_TOURS, ROUTE_SERVICE_INTERNATIONALS, ROUTE_SERVICE_EDIT, ROUTE_SERVICE_TRAVEL_DATE, ROUTE_SERVICE_EXIT_PLACES, ROUTE_SERVICE_EXIT_PLACE_PRICES, ROUTE_SERVICE_LOCKEDS, ROUTE_SERVICE_LOCKED_SHOW, ROUTE_SERVICE_GROUPS, ROUTE_SERVICE_GROUP_SHOW], [
                    { title: 'Hoteles', to: '/admin/servicios/hoteles', visible: hasService(SERVICE_TYPE_BY_ID.SERVICE_HOTELS), routes: [ROUTE_SERVICE_HOTELS] },
                    { title: 'Circuitos', to: '/admin/servicios/circuitos', visible: hasService(SERVICE_TYPE_BY_ID.SERVICE_CIRCUITS), routes: [ROUTE_SERVICE_CIRCUITS] },
                    { title: 'Pasadias', to: '/admin/servicios/pasadias', visible: hasService(SERVICE_TYPE_BY_ID.SERVICE_DAYTRIPS), routes: [ROUTE_SERVICE_DAYTRIPS] },
                    { title: 'Tours', to: '/admin/servicios/tours', visible: hasService(SERVICE_TYPE_BY_ID.SERVICE_TOURS), routes: [ROUTE_SERVICE_TOURS] },
                    { title: 'Internacionales', to: '/admin/servicios/internacionales', visible: hasService(SERVICE_TYPE_BY_ID.SERVICE_INTERNATIONAL), routes: [ROUTE_SERVICE_INTERNATIONALS] },
                    { title: 'Bloqueos', to: '/admin/servicios/bloqueos', visible: hasService(SERVICE_TYPE_BY_ID.SERVICE_LOCKEDS), routes: [ROUTE_SERVICE_LOCKEDS, ROUTE_SERVICE_LOCKED_SHOW] },
                    { title: 'Grupos', to: '/admin/servicios/grupos', visible: hasService(SERVICE_TYPE_BY_ID.SERVICE_GROUPS), routes: [ROUTE_SERVICE_GROUPS, ROUTE_SERVICE_GROUP_SHOW] },
                ]),
                link(SERVICE_QUOTE, '/admin/cotizador', [ROUTE_SERVICE_QUOTE, ROUTE_SERVICE_QUOTE_ADD]),
                group(RESERVATIONS, GROUP_RESERVATIONS, [ROUTE_RESERVATION_HOTEL_INDEX, ROUTE_RESERVATION_HOTEL_CREATE, ROUTE_RESERVATION_HOTEL_SHOW, ROUTE_RESERVATION_HOTEL_EDIT, ROUTE_RESERVATION_CIRCUIT_INDEX, ROUTE_RESERVATION_CIRCUIT_CREATE, ROUTE_RESERVATION_CIRCUIT_SHOW, ROUTE_RESERVATION_CIRCUIT_EDIT, ROUTE_RESERVATION_DAYTRIPS_INDEX, ROUTE_RESERVATION_DAYTRIPS_CREATE, ROUTE_RESERVATION_DAYTRIPS_SHOW, ROUTE_RESERVATION_DAYTRIPS_EDIT, ROUTE_RESERVATION_INTERNATIONALS_INDEX, ROUTE_RESERVATION_INTERNATIONALS_CREATE, ROUTE_RESERVATION_INTERNATIONALS_SHOW, ROUTE_RESERVATION_INTERNATIONALS_EDIT, ROUTE_RESERVATION_FLIGHTS_INDEX, ROUTE_RESERVATION_FLIGHTS_CREATE, ROUTE_RESERVATION_FLIGHTS_SHOW, ROUTE_RESERVATION_FLIGHTS_EDIT, ROUTE_RESERVATION_OTHER_SERVICE_INDEX, ROUTE_RESERVATION_OTHER_SERVICE_CREATE, ROUTE_RESERVATION_OTHER_SERVICE_SHOW, ROUTE_RESERVATION_OTHER_SERVICE_EDIT, ROUTE_RESERVATION_SELECT_SEAT_INDEX, ROUTE_RESERVATION_NOTES, ROUTE_RESERVATION_PEOPLE, ROUTE_RESERVATION_PAYMENTS, ROUTE_RESERVATION_LIST_PAYMENTS, ROUTE_RESERVATION_PAYMENT_TO_BROOKER, ROUTE_RESERVATION_TRANSFER_INDEX, ROUTE_RESERVATION_TRANSFER_CREATE, ROUTE_RESERVATION_TRANSFER_SHOW, ROUTE_RESERVATION_TRANSFER_EDIT, ROUTE_RESERVATION_LOCKEDS_INDEX, ROUTE_RESERVATION_LOCKEDS_CREATE, ROUTE_RESERVATION_LOCKEDS_SHOW, ROUTE_RESERVATION_LOCKEDS_EDIT, ROUTE_RESERVATION_GROUPS_INDEX, ROUTE_RESERVATION_GROUPS_CREATE, ROUTE_RESERVATION_GROUPS_SHOW, ROUTE_RESERVATION_GROUPS_EDIT], [
                    { title: 'Hoteles', to: '/admin/reservaciones/hoteles', visible: ACCESS_MENU_ACTION(HOTEL_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_HOTEL_INDEX, ROUTE_RESERVATION_HOTEL_CREATE, ROUTE_RESERVATION_HOTEL_SHOW, ROUTE_RESERVATION_HOTEL_EDIT] },
                    { title: 'Circuitos', to: '/admin/reservaciones/circuitos', visible: ACCESS_MENU_ACTION(CIRCUIT_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_CIRCUIT_INDEX, ROUTE_RESERVATION_CIRCUIT_CREATE, ROUTE_RESERVATION_CIRCUIT_SHOW, ROUTE_RESERVATION_CIRCUIT_EDIT] },
                    { title: 'Pasadias', to: '/admin/reservaciones/pasadias', visible: ACCESS_MENU_ACTION(DAYTRIPS_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_DAYTRIPS_INDEX, ROUTE_RESERVATION_DAYTRIPS_CREATE, ROUTE_RESERVATION_DAYTRIPS_SHOW, ROUTE_RESERVATION_DAYTRIPS_EDIT] },
                    { title: 'Tours', to: '/admin/reservaciones/tours', visible: ACCESS_MENU_ACTION(TOURS_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_TOURS_INDEX, ROUTE_RESERVATION_TOURS_CREATE, ROUTE_RESERVATION_TOURS_SHOW, ROUTE_RESERVATION_TOURS_EDIT] },
                    { title: 'Internacionales', to: '/admin/reservaciones/internacionales', visible: ACCESS_MENU_ACTION(INTERNATIONAL_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_INTERNATIONALS_INDEX, ROUTE_RESERVATION_INTERNATIONALS_CREATE, ROUTE_RESERVATION_INTERNATIONALS_SHOW, ROUTE_RESERVATION_INTERNATIONALS_EDIT] },
                    { title: 'Vuelos', to: '/admin/reservaciones/vuelos', visible: ACCESS_MENU_ACTION(FLIGHT_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_FLIGHTS_INDEX, ROUTE_RESERVATION_FLIGHTS_CREATE, ROUTE_RESERVATION_FLIGHTS_SHOW, ROUTE_RESERVATION_FLIGHTS_EDIT] },
                    { title: 'Otros Servicios', to: '/admin/reservaciones/otros-servicios', visible: ACCESS_MENU_ACTION(OTHER_SERVICES_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_OTHER_SERVICE_INDEX, ROUTE_RESERVATION_OTHER_SERVICE_CREATE, ROUTE_RESERVATION_OTHER_SERVICE_SHOW, ROUTE_RESERVATION_OTHER_SERVICE_EDIT] },
                    { title: 'Empaquetados', to: '/admin/reservaciones/empaquetados', visible: ACCESS_MENU_ACTION(PACKAGED_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_PACKAGED_INDEX, ROUTE_RESERVATION_PACKAGED_CREATE, ROUTE_RESERVATION_PACKAGED_SHOW, ROUTE_RESERVATION_PACKAGED_EDIT] },
                    { title: 'Traslados', to: '/admin/reservaciones/traslados', visible: ACCESS_MENU_ACTION(OTHER_SERVICES_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_TRANSFER_INDEX, ROUTE_RESERVATION_TRANSFER_CREATE, ROUTE_RESERVATION_TRANSFER_SHOW, ROUTE_RESERVATION_TRANSFER_EDIT] },
                    { title: 'Bloqueos', to: '/admin/reservaciones/bloqueos', visible: ACCESS_MENU_ACTION(LOCKED_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_LOCKEDS_INDEX, ROUTE_RESERVATION_LOCKEDS_CREATE, ROUTE_RESERVATION_LOCKEDS_EDIT, ROUTE_RESERVATION_LOCKEDS_SHOW] },
                    { title: 'Grupos', to: '/admin/reservaciones/grupos', visible: ACCESS_MENU_ACTION(GROUP_RESERVATIONS_INDEX), routes: [ROUTE_RESERVATION_GROUPS_INDEX, ROUTE_RESERVATION_GROUPS_CREATE, ROUTE_RESERVATION_GROUPS_EDIT, ROUTE_RESERVATION_GROUPS_SHOW] },
                ]),
                link(PAYMENT_MULTIPLE, '/admin/pagos-multiples', [ROUTE_PAYMENT_MULTIPLE, ROUTE_PAYMENT_MULTIPLE_ADD]),
                link(COUPON, '/admin/cupones', [ROUTE_COUPONS, ROUTE_COUPON_ADD]),
                link(BOX, '/admin/cajas', [ROUTE_BRANCH_BOXES, ROUTE_BOX_HISTORY, ROUTE_BOX_HISTORY_MOVE_LIST]),
                link(SAVING, '/admin/ahorros', [ROUTE_SAVING]),
                link(PROMOTIONS, '/admin/promociones', [ROUTE_PROMOTION_FOLDER, ROUTE_PROMOTIONS, ROUTE_PROMOTION_ADD, ROUTE_PROMOTION_EDIT]),
                link(BANKS, '/admin/cuentas', [ROUTE_BANKS, ROUTE_BANK_ADD, ROUTE_BANK_EDIT, ROUTE_BANK_HISTORY_MOVE_LIST]),
                link(PROFILES, '/admin/perfiles', [ROUTE_PROFILE_LIST, ROUTE_PROFILE_ADD]),
                link(ACTIVITY, '/admin/actividades', [ROUTE_ACTIVITY]),
                link(REPORTS, '/admin/reportes', [ROUTE_REPORTS]),
                link(TRAVEL_GALLERY, '/admin/galeria-viajes', [ROUTE_TRAVEL_GALLERY_INDEX]),
            ].filter(Boolean);
        },
    },
}
</script>
